import { AnalyticsProvider } from '~/utils/analytics-provider';

/**
 * Google Optimize Analytics Provider
 * Professional implementation following provider interface
 */
export default class GoogleOptimizeProvider extends AnalyticsProvider {
  constructor() {
    super('Google Optimize');
    this.optimizeLoaded = false;
  }

  /**
   * Initialize Google Optimize
   */
  initialize(config, propertyContext) {
    super.initialize(config, propertyContext);

    if (typeof window === 'undefined') {
      this.error('Cannot initialize Google Optimize in SSR environment');
      return;
    }

    this.loadOptimizeScript();
  }

  /**
   * Load Google Optimize script
   */
  loadOptimizeScript() {
    if (window.gtag && this.config.container_id) {
      // Google Optimize integrates with Google Analytics
      window.gtag('config', this.config.container_id);
      this.optimizeLoaded = true;
      this.log('Google Optimize configured with container:', this.config.container_id);
    } else {
      this.error('gtag not available or container_id missing');
    }
  }

  /**
   * Track events using Google Optimize (through gtag)
   */
  track(eventName, eventData) {
    if (!this.isReady() || !window.gtag) {
      this.error('Provider not ready or gtag not available');
      return;
    }

    const optimizeEvent = this.mapEvent(eventName);
    const optimizeData = this.formatEventData(eventName, eventData);

    try {
      if (optimizeData) {
        window.gtag('event', optimizeEvent, optimizeData);
      } else {
        window.gtag('event', optimizeEvent);
      }

      this.log(`Event tracked: ${optimizeEvent}`, optimizeData);
    } catch (error) {
      this.error('Failed to track event:', error);
    }
  }

  /**
   * Map standard events to Google Optimize events
   */
  mapEvent(standardEvent) {
    const optimizeEventMap = {
      'page_view': 'page_view',
      'view_item': 'view_item',
      'add_to_cart': 'add_to_cart',
      'remove_from_cart': 'remove_from_cart',
      'begin_checkout': 'begin_checkout',
      'add_payment_info': 'add_payment_info',
      'purchase': 'purchase',
      'search': 'search',
      'view_item_list': 'view_item_list'
    };

    return optimizeEventMap[standardEvent] || standardEvent;
  }

  /**
   * Format event data for Google Optimize
   */
  formatEventData(eventName, eventData) {
    const ecommerce = this.extractEcommerceData(eventData);

    switch (eventName) {
      case 'view_item':
        return {
          currency: ecommerce.currency || 'EUR',
          value: ecommerce.value || 0,
          items: ecommerce.items || [],
          custom_parameters: {
            property_name: this.propertyContext?.property_name
          }
        };

      case 'add_to_cart':
      case 'remove_from_cart':
        return {
          currency: ecommerce.currency || 'EUR',
          value: ecommerce.value || 0,
          items: ecommerce.items || [],
          custom_parameters: {
            property_name: this.propertyContext?.property_name
          }
        };

      case 'begin_checkout':
        return {
          currency: ecommerce.currency || 'EUR',
          value: ecommerce.value || 0,
          items: ecommerce.items || [],
          custom_parameters: {
            property_name: this.propertyContext?.property_name,
            checkout_step: 1
          }
        };

      case 'purchase':
        return {
          transaction_id: ecommerce.transaction_id,
          value: ecommerce.value || 0,
          currency: ecommerce.currency || 'EUR',
          items: ecommerce.items || [],
          custom_parameters: {
            property_name: this.propertyContext?.property_name
          }
        };

      case 'search':
        return {
          search_term: eventData.search_term || '',
          custom_parameters: {
            property_name: this.propertyContext?.property_name
          }
        };

      default:
        return {
          custom_parameters: {
            property_name: this.propertyContext?.property_name
          }
        };
    }
  }

  /**
   * Check if provider is ready
   */
  isReady() {
    return super.isReady() && this.optimizeLoaded && window.gtag;
  }
}